/**
 * services/notify.ts · Hand-off 通知系统 (替代 v200 TN_HANDOFF)
 *
 * 设计:
 *   - 业务 workflow 触发 sendNotification(roles[], opts)
 *   - INSERT notifications 表 · 给指定角色每人一条
 *   - 顶 NAV 铃铛 polling 30s 显未读数
 *   - 用户进 #notifications 查看 · 点击标已读
 *
 * 复用 v200 TN_HANDOFF 8 SEED 规则:
 *   - so_created → notify [engineer, purchasing]
 *   - wo_planned → notify [supervisor, worker]
 *   - wo_completed → notify [quality, warehouse]
 *   - iqc_passed → notify [warehouse]
 *   - oqc_passed → notify [warehouse, sales]
 *   - shipped → notify [sales, finance]
 *   - ar_issued → notify [finance]
 *   - payment_received → notify [sales, gm, boss]
 */

import { query, getDb, saveDb } from '@core/db';

export type Severity = 'info' | 'warning' | 'critical';

export interface NotifyOpts {
  title: string;
  body?: string;
  severity?: Severity;
  refType?: string;  // 'sales_order' / 'work_order' / 'invoice' / ...
  refId?: number;
  refHash?: string;  // 用于点击跳转 (e.g. 'order360?id=42')
}

export interface Notification {
  id: number;
  recipient_role: string | null;
  recipient_code: string | null;
  title: string;
  body: string | null;
  severity: Severity;
  ref_type: string | null;
  ref_id: number | null;
  ref_hash: string | null;
  read_at: string | null;
  created_at: string;
}

/**
 * 给多角色发通知 (每角色每人一条 · employees 表查所有 role 匹配 user)
 */
export function sendNotification(roles: string[], opts: NotifyOpts): number {
  if (!roles.length) return 0;
  const db = getDb();
  const sev = opts.severity || 'info';
  let inserted = 0;

  // 每角色 INSERT 1 条 (recipient_code=null · 全 role 可见 · 简化版)
  // 真业务版可扩 SELECT employees WHERE role IN (...) 每人 1 条
  for (const role of roles) {
    try {
      db.run(
        `INSERT INTO notifications (recipient_role, title, body, severity, ref_type, ref_id, ref_hash, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'))`,
        [role, opts.title, opts.body || null, sev, opts.refType || null, opts.refId || null, opts.refHash || null]
      );
      inserted++;
    } catch {
      /* 表不存在 (老 db 未跑 migration 010) 跳过, 不阻塞 workflow */
    }
  }
  void saveDb();
  return inserted;
}

/**
 * 给指定用户发通知 (个人 · recipient_code 精确)
 */
export function sendToUser(userCode: string, opts: NotifyOpts): boolean {
  try {
    getDb().run(
      `INSERT INTO notifications (recipient_code, title, body, severity, ref_type, ref_id, ref_hash, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'))`,
      [userCode, opts.title, opts.body || null, opts.severity || 'info',
       opts.refType || null, opts.refId || null, opts.refHash || null]
    );
    void saveDb();
    return true;
  } catch {
    return false;
  }
}

/**
 * 当前用户未读数 (顶 NAV 铃铛红 badge 用)
 * · 匹配 role OR userCode (任一)
 */
export function getUnreadCount(userRole: string, userCode: string): number {
  const r = query<{ c: number }>(
    `SELECT COUNT(*) AS c FROM notifications
     WHERE (recipient_role = ? OR recipient_code = ?)
       AND read_at IS NULL`,
    [userRole, userCode]
  );
  return r[0]?.c || 0;
}

/**
 * 列表 · 当前用户可见通知 (限 100 条)
 */
export function listForUser(userRole: string, userCode: string, opts: { onlyUnread?: boolean; limit?: number } = {}): Notification[] {
  const limit = opts.limit || 100;
  const unreadFilter = opts.onlyUnread ? ' AND read_at IS NULL' : '';
  return query<Notification>(
    `SELECT * FROM notifications
     WHERE (recipient_role = ? OR recipient_code = ?)${unreadFilter}
     ORDER BY id DESC LIMIT ?`,
    [userRole, userCode, limit]
  );
}

/**
 * 标已读 · 单条
 */
export function markRead(id: number): boolean {
  try {
    getDb().run(`UPDATE notifications SET read_at = datetime('now') WHERE id = ?`, [id]);
    void saveDb();
    return true;
  } catch {
    return false;
  }
}

/**
 * 标已读 · 全部当前用户
 */
export function markAllRead(userRole: string, userCode: string): number {
  try {
    const db = getDb();
    db.run(
      `UPDATE notifications SET read_at = datetime('now')
       WHERE (recipient_role = ? OR recipient_code = ?) AND read_at IS NULL`,
      [userRole, userCode]
    );
    void saveDb();
    return 1;
  } catch {
    return 0;
  }
}
